/**
 * Beetzee Payment Service
 * Handles transaction creation and payment processing with Beetzee
 */

const BEETZEE_API_BASE = process.env.REACT_APP_BEETZEE_API_URL;

// Get Beetzee API token from server-side environment
// Since this is client-side code, we need to pass the token from server or use server-side API calls
const getBeetzeeToken = () => {
  // Token should not be accessible from client-side
  // We'll handle API calls through server endpoints instead
  return '';
};

/**
 * Generate signature for Beetzee API request
 * Following PHP example: password_hash($secretKey . '::' . $referenceNo . '::' . $amount, PASSWORD_BCRYPT, ['cost' => 10])
 * Since bcrypt can't run in browser, we use server endpoint to generate signature
 * @param {string} secretKey - Merchant secret key (optional, server will use environment if not provided)
 * @param {string} referenceNo - Merchant reference number
 * @param {number} amount - Transaction amount
 * @returns {Promise<string>} Generated bcrypt signature
 */
export const generateBeetzeeSignature = async (secretKey, referenceNo, amount) => {
  try {
    // Use server endpoint to generate signature (bcrypt can't run in browser)
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    
    const requestData = {
      reference_no: referenceNo,
      amount: amount
    };
    
    const response = await fetch(`${API_BASE_URL}/api/beetzee/generate-signature`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to generate signature: ${response.status} - ${errorText}`);
    }
    
    const result = await response.json();
    
    if (!result.success || !result.signature) {
      throw new Error('Invalid signature response from server');
    }
    
    return result.signature;
    
  } catch (error) {
    console.error('Signature generation error:', error);
    throw new Error('Failed to generate payment signature');
  }
};

/**
 * Create a payment transaction with Beetzee
 * @param {Object} paymentData - Payment information
 * @param {number} paymentData.amount - Amount in PHP
 * @param {string} paymentData.merchant_ref_code - Unique reference code from merchant
 * @param {string} paymentData.first_name - First name of payor (optional)
 * @param {string} paymentData.last_name - Last name of payor (optional)
 * @param {string} paymentData.mobile - Mobile number of payor (optional)
 * @param {string} paymentData.email - Email address of payor (optional)
 * @param {string} paymentData.webhook_url - Webhook URL for transaction updates
 * @param {string} paymentData.success_redirect_url - Success redirect URL
 * @param {string} paymentData.failed_redirect_url - Failed redirect URL
 * @param {string} paymentData.cancel_redirect_url - Cancel redirect URL
 * @param {number} paymentData.expires_in_minutes - Transaction validity in minutes
 * @param {number} paymentData.link_expires_in_minutes - Link validity in minutes
 * @param {string} paymentData.signature - Generated signature
 * @returns {Promise<Object>} Transaction response from Beetzee
 */
export const createBeetzeeTransaction = async (paymentData) => {
  try {
    const { 
      amount,
      merchant_ref_code: merchantRefCode,
      first_name: firstName,
      last_name: lastName,
      mobile,
      email,
      webhook_url: webhookUrl,
      success_redirect_url: successRedirectUrl,
      failed_redirect_url: failedRedirectUrl,
      cancel_redirect_url: cancelRedirectUrl,
      expires_in_minutes: expiresInMinutes = 1, // Default 1 minute
      link_expires_in_minutes: linkExpiresInMinutes = 1, // Default 1 minute
      signature
    } = paymentData;
    
    // Validate required fields
    if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
      throw new Error(`Invalid payment amount: ${amount}. Amount must be a positive number greater than 0.`);
    }
    
    if (!merchantRefCode) {
      throw new Error('Merchant reference code is required');
    }

    if (!webhookUrl) {
      throw new Error('Webhook URL is required');
    }

    if (!successRedirectUrl) {
      throw new Error('Success redirect URL is required');
    }

    if (!failedRedirectUrl) {
      throw new Error('Failed redirect URL is required');
    }

    if (!cancelRedirectUrl) {
      throw new Error('Cancel redirect URL is required');
    }

    if (!signature) {
      throw new Error('Signature is required');
    }

    // Prepare transaction data for Beetzee API
    const transactionData = {
      signature,
      merchant_ref_code: merchantRefCode,
      amount: parseFloat(amount),
      webhook_url: webhookUrl,
      success_redirect_url: successRedirectUrl,
      failed_redirect_url: failedRedirectUrl,
      cancel_redirect_url: cancelRedirectUrl,
      expires_in_minutes: parseInt(expiresInMinutes),
      link_expires_in_minutes: parseInt(linkExpiresInMinutes)
    };

    // Add optional fields if provided
    if (firstName) transactionData.first_name = firstName;
    if (lastName) transactionData.last_name = lastName;
    if (mobile) transactionData.mobile = mobile;
    if (email) transactionData.email = email;

    // Use server endpoint instead of calling Beetzee API directly
    // This way the API token stays secure on the server
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    const response = await fetch(`${API_BASE_URL}/api/beetzee/create-transaction`, {
      method: 'POST',
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json'
      },
      body: JSON.stringify(transactionData)
    });

    if (!response.ok) {
      const errorText = await response.text();
      
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { message: errorText };
      }
      
      throw new Error(`Transaction creation failed: ${response.status} - ${errorData.message || errorData.error || errorText || 'Unknown error'}`);
    }

    const transactionResponse = await response.json();

    return {
      success: transactionResponse.success || true,
      transactionId: transactionResponse.data?.ref_code || transactionResponse.ref_code,
      paymentUrl: transactionResponse.data?.payment_url || transactionResponse.payment_url || `https://pay-st.beetzee.com/${transactionResponse.data?.ref_code}`,
      amount: transactionResponse.data?.amount || transactionResponse.amount,
      currency: 'PHP',
      reference: transactionResponse.data?.ref_code || transactionResponse.ref_code,
      status: transactionResponse.data?.status || transactionResponse.status,
      message: transactionResponse.message,
      data: transactionResponse
    };

  } catch (error) {
    console.error('Beetzee transaction creation error:', error);
    return {
      success: false,
      error: error.message || 'Failed to create payment transaction'
    };
  }
};

/**
 * Check payment status of a transaction
 * @param {string} transactionId - Beetzee transaction ID
 * @returns {Promise<Object>} Payment status response
 */
export const checkBeetzeeTransactionStatus = async (transactionId) => {
  try {
    if (!transactionId) {
      throw new Error('Transaction ID is required');
    }

    const beetzeeToken = getBeetzeeToken();
    if (!beetzeeToken) {
      throw new Error('Beetzee API token is not configured');
    }

    const response = await fetch(`${BEETZEE_API_BASE}/transactions/${transactionId}`, {
      method: 'GET',
      headers: {
        'accept': 'application/json',
        'content-type': 'application/json',
        'Authorization': `Bearer ${beetzeeToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to check transaction status: ${response.status}`);
    }

    const statusData = await response.json();
    
    return {
      success: true,
      transactionId: statusData.id || statusData.transaction_id,
      status: statusData.status,
      paid: statusData.status === 'completed' || statusData.status === 'paid',
      amount: statusData.amount,
      currency: statusData.currency,
      paidAt: statusData.completed_at || statusData.paid_at,
      data: statusData
    };

  } catch (error) {
    console.error('Transaction status check error:', error);
    return {
      success: false,
      error: error.message || 'Failed to check transaction status'
    };
  }
};

/**
 * Format amount for display
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code
 * @returns {string} Formatted amount string
 */
export const formatAmount = (amount, currency = 'PHP') => {
  const formatter = new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return formatter.format(amount);
};

export default {
  generateBeetzeeSignature,
  createBeetzeeTransaction,
  checkBeetzeeTransactionStatus,
  formatAmount
};